import moment from 'moment';
import { detectLanguage } from 'javascript/sf/detectLanguage';

const hourInMilliseconds = 1 * 60 * 60 * 1000;
const t24HoursInMilliseconds = 24 * hourInMilliseconds;
const noonStartInMilliSeconds = t24HoursInMilliseconds / 2;
const eveningStartInMilliSeconds = noonStartInMilliSeconds + 5 * hourInMilliseconds;
const dateFormat = 'dddd, LL'; // Localized date with day of week
const logoOverlapBreakpoint = 960;

export const bannerContentHeight = 40;
const bannerContentWidth = 120;

export const getDayTime = () => {
	const currentHour = new Date().getHours();
	if (currentHour < 12) {
		return 'morning';
	} else if (currentHour < 17) {
		return 'afternoon';
	} else {
		return 'evening';
	}
};

export const getGreetingRefreshTimeInMilliseconds = () => {
	const nowInMilliSeconds =
		Date.now() - new Date(moment().format('MM/DD/yyyy')).getTime();

	if (nowInMilliSeconds < noonStartInMilliSeconds) {
		return noonStartInMilliSeconds - nowInMilliSeconds;
	} else if (nowInMilliSeconds < eveningStartInMilliSeconds) {
		return eveningStartInMilliSeconds - nowInMilliSeconds;
	} else {
		return t24HoursInMilliseconds - nowInMilliSeconds;
	}
};

export const getTodayString = () => {
	return moment().locale(detectLanguage()).format(dateFormat);
};

export const areSiblings = (element1: Element, element2: Element): boolean => {
	return (
		element1?.parentElement === element2?.parentElement &&
		element1?.parentElement !== null
	);
};

interface ImageDetails {
	width: number;
	height: number;
	aspectRatio: number;
	renderedWidth: number;
}

export interface ImageDimensions {
	width: string;
	height: string;
}

const getImageLoadDetails = ({
	imageUrl,
	fixedHeight = 0,
}: {
	imageUrl: string;
	fixedHeight?: number;
}): Promise<ImageDetails> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const aspectRatio = img.naturalWidth / img.naturalHeight;
			const renderedWidth = fixedHeight * aspectRatio;
			resolve({
				renderedWidth,
				width: img.width,
				height: img.height,
				aspectRatio,
			});
		};
		img.onerror = () => {
			reject(new Error('Failed to load image.'));
		};
		img.src = imageUrl;
	});
};

export const computeImageDimensions = async (
	imageUrl: string,
	imageWidth: number = bannerContentWidth,
	imageHeight: number = bannerContentHeight
): Promise<ImageDimensions> => {
	try {
		const imageDetails = await getImageLoadDetails({ imageUrl });
		return imageDetails.aspectRatio > 3
			? { width: `${imageWidth}px`, height: 'auto' }
			: { width: 'auto', height: `${imageHeight}px` };
	} catch {
		return { width: `${imageWidth}px`, height: `${imageHeight}px` };
	}
};

const calcOverlapBreakpoint = (
	element1Width: number,
	element2Width: number,
	minimumGap = 20,
	padding = 48
): number => {
	const breakpointFullSearch =
		(element1Width + minimumGap + element2Width / 2) * 2 + padding;

	return breakpointFullSearch;
};

export const calcLogoOverlapBreakpoint = async (
	imageUrl: string,
	searchBarWidth: number
): Promise<number> => {
	try {
		const logoDims = await getImageLoadDetails({
			imageUrl,
			fixedHeight: bannerContentHeight,
		});
		return calcOverlapBreakpoint(logoDims.renderedWidth, searchBarWidth);
	} catch (error) {
		console.error('Failed to initialize logo breakpoint:', error);
		return logoOverlapBreakpoint;
	}
};
